import React from "react";
import { motion } from "framer-motion";
import { AboutUs, Aboutbg } from "../assets";
import { FaCheckCircle } from "react-icons/fa";

export const fadeUp = {
  hidden: { opacity: 0, y: 50 },
  visible: (i = 1) => ({
    opacity: 1,
    y: 0,
    transition: { delay: 0.1 * i, duration: 0.6, ease: "easeOut" },
  }),
};

const About = () => {
  return (
    <div className="bg-white text-gray-800">
      {/* Header Section with background image */}
      <header 
        className="relative h-64 md:h-72 bg-cover bg-center text-white flex flex-col items-center justify-center text-center px-6 w-full"
        style={{ backgroundImage: `url(${Aboutbg})` }}
      >
        <div className="absolute inset-0 bg-blue-900 bg-opacity-70"></div>
        <motion.div
          className="relative z-10 max-w-3xl"
          initial={{ opacity: 0, y: -40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-5xl md:text-6xl font-extrabold mb-4 tracking-tight">
            About Us
          </h1>
          <p className="text-xl opacity-90">Discover who we are, what we build, and why we lead.</p>
        </motion.div>
      </header>

      {/* Who We Are */}
      <section className="max-w-7xl mx-auto py-20 px-6 grid grid-cols-1 md:grid-cols-2 gap-14 items-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <img src={AboutUs} alt="ABOUT MICRO-WELL INDUSTRIES" className="w-full rounded-3xl shadow-2xl" />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 100 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="text-4xl font-bold mb-4 text-blue-900">Who We Are</h2>
          <p className="text-lg text-gray-700 leading-relaxed mb-2">
            <strong>MICRO-WELL INDUSTRIES,</strong> established in 2015 and based in Sidhpur, Gujarat, India. Our prominent manufacturer, supplier, and exporter of a comprehensive range of sealing & pump solutions. Much like other industry leaders, we specialize in high-quality Mechanical Shaft Seals, Pump & Pumps Component. Our commitment is to provide reliable and efficient sealing & pump solutions to a wide spectrum of industries.
          </p>
          <p className="text-lg text-gray-700 leading-relaxed mb-2">
            Our diverse product line includes pump mechanical seals, pusher and non-pusher mechanical seals, and single and double mechanical seals. We offer both balanced and unbalanced designs, along with cartridge mechanical seals specifically tailored for various applications, including agitators and mixers.
          </p>
          <p className="text-lg text-gray-700 leading-relaxed">
            Our mechanical seals are categorized by their spring types, including conical spring, single spring, multi-spring, and wave spring designs. We also offer a variety of bellow type mechanical seals, such as rubber, PTFE, and metal bellow seals, to meet different operational requirements.          </p>
        </motion.div>
      </section>

      {/* Quality & Leadership */}
      <section className="bg-gray-50 py-20 px-6">
        <div className="max-w-6xl mx-auto space-y-14 text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
          >
            <h3 className="text-3xl font-bold text-blue-800 mb-3">Quality & Innovation</h3>
            <p className="text-gray-700 text-lg max-w-3xl mx-auto">
              We continuously update our product line to meet evolving market needs through advanced R&D and strict quality control.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            custom={2}
            variants={fadeUp}
          >
            <h3 className="text-3xl font-bold text-blue-800 mb-3">Leadership That Drives</h3>
            <p className="text-gray-700 text-lg max-w-3xl mx-auto">
              Under the leadership of <strong>Mr. Mohammad Kadiwal</strong>, our journey has been marked by vision, resilience, and growth.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Product Portfolio */}
      <motion.section
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeUp}
        className="py-20 px-6 bg-gradient-to-br from-sky-50 to-white"
      >
        <div className="max-w-6xl mx-auto text-center">
          <h3 className="text-3xl font-bold text-blue-900 mb-6">Product Portfolio</h3>
          <p className="text-gray-700 text-lg max-w-4xl mx-auto mb-8">
            Our seals & pump are trusted across industries — from automobiles to marine — known for their high performance, durability, and precision.
          </p>
          <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto text-left">
            {[
              "Precision-engineered and industry-compliant",
              "Custom sizes and specifications available",
              "Built with high-efficiency materials",
              "Affordable pricing for all scales",
            ].map((item, i) => (
              <motion.p
                key={i}
                className="flex items-start gap-3 text-gray-700 text-lg"
                custom={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
              >
                <FaCheckCircle className="text-blue-600 mt-1" />
                {item}
              </motion.p>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Our Team */}
      <motion.section
        className="py-20 px-6 bg-white"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeUp}
      >
        <div className="max-w-5xl mx-auto text-center">
          <h3 className="text-3xl font-bold text-blue-900 mb-6">Our Team</h3>
          <p className="text-lg text-gray-700 leading-relaxed">
            Our expert team is our strongest asset. With deep market understanding and technical excellence, they work hand-in-hand with clients to deliver tailored solutions.
          </p>
        </div>
      </motion.section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gray-100 px-6">
        <motion.div
          className="max-w-6xl mx-auto text-center"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
        >
          <h3 className="text-3xl font-bold mb-10 text-blue-900">Why Choose Us?</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              "Premium quality components",
              "State-of-the-art infrastructure",
              "Skilled technical workforce",
              "Fully customized solutions",
              "Assured on-time delivery",
              "Competitive pricing",
            ].map((point, i) => (
              <motion.div
                key={i}
                className="bg-white p-6 rounded-xl shadow hover:shadow-md transition border"
                custom={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
              >
                <p className="text-gray-700 text-lg flex items-start gap-2">
                  <FaCheckCircle className="text-green-500 mt-1" />
                  {point}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Mission / Vision */}
      <motion.section
        className="py-16 px-6"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeUp}
      >
        <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-xl border border-gray-200 text-center p-10">
          <h3 className="text-2xl font-bold mb-4 text-blue-900">Our Mission / Vision</h3>
          <p className="text-gray-700 text-lg leading-relaxed">
            To be the most trusted and innovative provider of Mechanical Shaft Seals, Pump & Pumps Component by delivering unmatched quality, precision, and service.
          </p>
        </div>
      </motion.section>
    </div>
  );
};

export default About;